import type { AggregateFunction, Cadence, FormulaExpression, Metric } from './types';
import { referenceProblem, trailingPeriodsProblem, type FormulaMetric, type ReferenceContext } from './formula-guidance';

// Formula source is the editor's text form. A metric is referenced as `{metricId}`;
// the editor renders each reference as a chip and never shows the identifier.

const AGGREGATE_FUNCTIONS: Record<string, AggregateFunction> = {
  SUM: 'sum',
  MEAN: 'mean',
  MEDIAN: 'median',
  MIN: 'minimum',
  MAX: 'maximum',
  LAST: 'last',
};

const FUNCTION_NAME = Object.fromEntries(
  Object.entries(AGGREGATE_FUNCTIONS).map(([name, fn]) => [fn, name]),
) as Record<AggregateFunction, string>;

export const metricReference = (metricId: string) => `{${metricId}}`;

type Token = { kind: 'number' | 'name' | 'reference' | 'operator' | 'punctuation'; value: string };

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;
  while (index < source.length) {
    const rest = source.slice(index);
    const whitespace = rest.match(/^\s+/);
    if (whitespace) { index += whitespace[0].length; continue; }
    const number = rest.match(/^(?:\d+(?:\.\d*)?|\.\d+)/);
    if (number) { tokens.push({ kind: 'number', value: number[0] }); index += number[0].length; continue; }
    const reference = rest.match(/^\{([^{}]+)\}/);
    if (reference) { tokens.push({ kind: 'reference', value: reference[1] }); index += reference[0].length; continue; }
    const name = rest.match(/^[A-Za-z][A-Za-z0-9]*/);
    if (name) { tokens.push({ kind: 'name', value: name[0] }); index += name[0].length; continue; }
    const character = rest[0];
    if ('+-*/'.includes(character)) tokens.push({ kind: 'operator', value: character });
    else if ('(),'.includes(character)) tokens.push({ kind: 'punctuation', value: character });
    else throw new Error(`"${character}" can't be used in a formula. Use + - * / and parentheses.`);
    index++;
  }
  return tokens;
}

/**
 * Parse formula source into an expression tree. `metrics` holds every
 * referenceable metric by id. With `cadence`, references are also checked
 * against the formula's result cadence. `self` is the formula's own id, which
 * may appear only inside PREV. `noun` is the workspace's term for a metric,
 * used in error messages.
 */
export function parseFormulaExpression(
  source: string,
  metrics: ReadonlyMap<string, FormulaMetric>,
  { noun = 'metric', cadence, self }: { noun?: string; cadence?: Cadence; self?: string } = {},
): FormulaExpression {
  const tokens = tokenize(source);
  let cursor = 0;

  const peek = () => tokens[cursor];
  const previous = () => tokens[cursor - 1];
  const fail = (message: string): never => { throw new Error(message); };
  const describe = (token: Token) =>
    token.kind === 'reference' ? `"${metrics.get(token.value)?.title ?? noun}"` : `"${token.value}"`;
  const expect = (value: string, message: string) => {
    if (peek()?.value !== value) fail(message);
    cursor++;
  };
  const resolve = (metricId: string, context: ReferenceContext) => {
    const metric = metrics.get(metricId);
    if (!metric) return fail(`A ${noun} in this formula has been deleted. Remove it and choose another.`);
    if (metricId === self && context.kind !== 'previous') {
      fail(`A formula can use itself only inside a function that doesn't reference the current period, like PREV.`);
    }
    const problem = cadence && referenceProblem(metric, context, cadence, noun);
    if (problem) fail(problem);
    return metric;
  };
  const takeMetric = (fn: string, context: ReferenceContext) => {
    const token = peek();
    if (token?.kind !== 'reference') fail(`${fn} needs a ${noun} inside its parentheses, like ${fn}(Revenue).`);
    cursor++;
    return { metricId: token!.value, metric: resolve(token!.value, context) };
  };

  const missingOperand = (): never => {
    const token = peek();
    const before = previous();
    if (!token) {
      if (before?.value === '(') fail(`Add a ${noun} or number inside the parentheses.`);
      if (before?.kind === 'operator') fail(`Add a ${noun} or number after "${before.value}".`);
      fail('The formula is incomplete.');
    }
    if (token!.value === ')' && before?.value === '(') fail('The parentheses are empty.');
    if (token!.value === ',') fail(`A comma can only follow the ${noun} inside SUM, MEAN, MEDIAN, MIN, or MAX.`);
    return fail(`Add a ${noun} or number before "${token!.value}".`);
  };

  const primary = (): FormulaExpression => {
    const token = peek();
    if (!token) return missingOperand();
    if (token.value === '(') {
      cursor++;
      const expression = additive();
      if (!peek()) fail('Add a closing parenthesis.');
      expect(')', `Add an operator (+ - * /) or a closing parenthesis before ${describe(peek()!)}.`);
      return expression;
    }
    if (token.kind === 'operator' && token.value === '-') {
      cursor++;
      const operand = primary();
      if (operand.type === 'constant') return { type: 'constant', value: -operand.value };
      return { type: 'operation', operator: '*', left: { type: 'constant', value: -1 }, right: operand };
    }
    if (token.kind === 'number') {
      cursor++;
      return { type: 'constant', value: Number(token.value) };
    }
    if (token.kind === 'reference') {
      cursor++;
      resolve(token.value, { kind: 'direct' });
      return { type: 'metric', metricId: token.value };
    }
    if (token.kind !== 'name') return missingOperand();
    cursor++;
    const name = token.value.toUpperCase();
    if (name !== 'PREV' && !AGGREGATE_FUNCTIONS[name]) {
      fail(`"${token.value}" isn't a ${noun} or a function. Choose a ${noun} from the suggestions as you type.`);
    }
    expect('(', `${name} needs a ${noun} in parentheses, like ${name}(Revenue).`);
    if (name === 'PREV') {
      const { metricId } = takeMetric(name, { kind: 'previous' });
      if (peek()?.value === ',') fail(`PREV takes only a ${noun}, not a number of periods.`);
      expect(')', 'Close PREV with ")".');
      return { type: 'previous', metricId };
    }
    const { metricId, metric } = takeMetric(name, { kind: 'aggregate', name });
    let trailingPeriods: number | undefined;
    if (peek()?.value === ',') {
      cursor++;
      const range = peek();
      if (!range || range.value === ')') fail(`Add the number of trailing periods after the comma in ${name}.`);
      cursor++;
      trailingPeriods = Number(range!.value);
      if (range!.kind !== 'number' || !Number.isInteger(trailingPeriods) || trailingPeriods < 1) {
        fail(`The number of trailing periods in ${name} must be a whole number, 1 or more.`);
      }
    }
    expect(')', `Close ${name} with ")".`);
    const trailingProblem = cadence && trailingPeriodsProblem(
      name, metric, trailingPeriods === undefined ? undefined : String(trailingPeriods), cadence,
    );
    if (trailingProblem) fail(trailingProblem);
    return { type: 'aggregate', function: AGGREGATE_FUNCTIONS[name], metricId, trailingPeriods };
  };

  const multiplicative = (): FormulaExpression => {
    let expression = primary();
    while (peek()?.value === '*' || peek()?.value === '/') {
      const operator = tokens[cursor++].value as '*' | '/';
      expression = { type: 'operation', operator, left: expression, right: primary() };
    }
    return expression;
  };
  const additive = (): FormulaExpression => {
    let expression = multiplicative();
    while (peek()?.value === '+' || peek()?.value === '-') {
      const operator = tokens[cursor++].value as '+' | '-';
      expression = { type: 'operation', operator, left: expression, right: multiplicative() };
    }
    return expression;
  };

  const expression = additive();
  const extra = peek();
  if (extra?.value === ')') fail('There is a ")" without a matching "(".');
  if (extra) fail(`Add an operator (+ - * /) before ${describe(extra)}.`);
  return expression;
}

const PRECEDENCE = { '+': 1, '-': 1, '*': 2, '/': 2 } as const;

/** Render an expression back to formula source, adding only the parentheses its shape needs. */
export function formulaSource(expression: FormulaExpression): string {
  const render = (node: FormulaExpression): string => {
    if (node.type === 'constant') return String(node.value);
    if (node.type === 'metric') return metricReference(node.metricId);
    if (node.type === 'previous') return `PREV(${metricReference(node.metricId)})`;
    if (node.type === 'aggregate') {
      const range = node.trailingPeriods ? `, ${node.trailingPeriods}` : '';
      return `${FUNCTION_NAME[node.function]}(${metricReference(node.metricId)}${range})`;
    }
    const precedence = PRECEDENCE[node.operator];
    const operand = (child: FormulaExpression, isRight: boolean) => {
      const text = render(child);
      if (child.type !== 'operation') return text;
      const childPrecedence = PRECEDENCE[child.operator];
      const wrap = childPrecedence < precedence || (isRight && childPrecedence === precedence);
      return wrap ? `(${text})` : text;
    };
    return `${operand(node.left, false)} ${node.operator} ${operand(node.right, true)}`;
  };
  return render(expression);
}

/**
 * The innermost function call enclosing `index` in formula source: where its
 * arguments open, the metric it references if one has been chosen, which
 * argument `index` falls in, and the trailing-periods argument as written.
 * Null outside any function.
 */
export function enclosingCall(source: string, index: number): {
  name: string;
  open: number;
  metricId?: string;
  argument: number;
  periodsText?: string;
} | null {
  const stack: { name: string | null; open: number }[] = [];
  for (const match of source.slice(0, index).matchAll(/\{[^{}]*\}|[A-Za-z][A-Za-z0-9]*\s*\(|\(|\)/g)) {
    const text = match[0];
    if (text === ')') stack.pop();
    else if (!text.startsWith('{')) {
      stack.push({ name: text === '(' ? null : text.replace(/\s*\($/, '').toUpperCase(), open: match.index! + text.length });
    }
  }
  const frame = stack.at(-1);
  if (!frame?.name) return null;
  const body = source.slice(frame.open);
  const reference = /^\s*\{([^{}]+)\}/.exec(body);
  const comma = /^[^,()]*,\s*([^\s(),+\-*/]*)/.exec(body);
  return {
    name: frame.name,
    open: frame.open,
    metricId: reference?.[1],
    argument: source.slice(frame.open, index).includes(',') ? 1 : 0,
    periodsText: comma?.[1],
  };
}

/**
 * How a derived metric reads as a formula, with metric titles in place of
 * references. An aggregate reads as the function call that would define it,
 * e.g. `SUM(Revenue, 4)`. Null for an input metric.
 */
export function formulaText(
  metric: Pick<Metric, 'origin' | 'formulaExpression' | 'aggregate'>,
  titleOf: (metricId: string) => string,
): string | null {
  const expression: FormulaExpression | undefined = metric.origin === 'aggregate' && metric.aggregate
    ? {
      type: 'aggregate',
      function: metric.aggregate.function,
      metricId: metric.aggregate.sourceMetricId,
      trailingPeriods: metric.aggregate.trailingPeriods,
    }
    : metric.origin === 'formula' ? metric.formulaExpression : undefined;
  return expression ? formulaSource(expression).replace(/\{([^{}]+)\}/g, (_, metricId: string) => titleOf(metricId)) : null;
}
